/**
 * デモモード制限機能のReact Hook
 * FL-154: デモ体験時の外部サービス無効化
 */

import { useMemo, useCallback } from 'react';
import {
  demoModeService,
  isFeatureRestricted,
  getRestrictionMessage,
} from '../services/demoModeService';
import { DemoFeature, RestrictionResult, ServiceType, MockResponse } from '../types/demoMode';

/**
 * デモモード制限機能のカスタムフック
 */
export const useDemoMode = () => {
  // デモモードの状態を取得
  const isEnabled = useMemo(() => demoModeService.isEnabled(), []);

  // 制限対象機能の一覧を取得
  const restrictedFeatures = useMemo(() => demoModeService.getRestrictedFeatures(), []);

  // 統計情報を取得
  const stats = useMemo(() => demoModeService.getStats(), []);

  // 機能制限チェック
  const checkRestriction = useCallback((feature: DemoFeature): RestrictionResult => {
    return demoModeService.checkRestriction(feature);
  }, []);

  // 機能が制限されているかの簡易チェック
  const isRestricted = useCallback((feature: DemoFeature): boolean => {
    return isFeatureRestricted(feature);
  }, []);

  // 制限メッセージを取得
  const getMessage = useCallback((feature: DemoFeature): string => {
    return getRestrictionMessage(feature);
  }, []);

  // モックレスポンスを取得
  const getMockResponse = useCallback((serviceType: ServiceType, options?: any): MockResponse => {
    return demoModeService.getMockResponse(serviceType, options);
  }, []);

  // デモアクションをログ出力
  const logAction = useCallback((feature: DemoFeature, action: string, details?: any): void => {
    demoModeService.logDemoAction(feature, action, details);
  }, []);

  return {
    // 状態
    isEnabled,
    restrictedFeatures,
    stats,

    // メソッド
    checkRestriction,
    isRestricted,
    getMessage,
    getMockResponse,
    logAction,
  };
};

/**
 * 特定機能用のカスタムフック
 */

/**
 * メール送信制限用フック
 */
export const useEmailRestriction = () => {
  const { isRestricted, getMessage, getMockResponse, logAction } = useDemoMode();

  const checkEmailRestriction = useCallback(() => {
    return isRestricted(DemoFeature.EMAIL_SEND);
  }, [isRestricted]);

  const getEmailMessage = useCallback(() => {
    return getMessage(DemoFeature.EMAIL_SEND);
  }, [getMessage]);

  const getEmailMockResponse = useCallback(
    (options?: any) => {
      return getMockResponse(ServiceType.EMAIL, options);
    },
    [getMockResponse]
  );

  const logEmailAction = useCallback(
    (action: string, details?: any) => {
      logAction(DemoFeature.EMAIL_SEND, action, details);
    },
    [logAction]
  );

  return {
    isEmailRestricted: checkEmailRestriction(),
    emailMessage: getEmailMessage(),
    getEmailMockResponse,
    logEmailAction,
  };
};

/**
 * ユーザー登録制限用フック
 */
export const useUserRegistrationRestriction = () => {
  const { isRestricted, getMessage, logAction } = useDemoMode();

  const checkRegistrationRestriction = useCallback(() => {
    return isRestricted(DemoFeature.USER_REGISTRATION);
  }, [isRestricted]);

  const getRegistrationMessage = useCallback(() => {
    return getMessage(DemoFeature.USER_REGISTRATION);
  }, [getMessage]);

  const logRegistrationAction = useCallback(
    (action: string, details?: any) => {
      logAction(DemoFeature.USER_REGISTRATION, action, details);
    },
    [logAction]
  );

  return {
    isRegistrationRestricted: checkRegistrationRestriction(),
    registrationMessage: getRegistrationMessage(),
    logRegistrationAction,
  };
};

/**
 * パスワードリセット制限用フック
 */
export const usePasswordResetRestriction = () => {
  const { isRestricted, getMessage, logAction } = useDemoMode();

  const checkPasswordResetRestriction = useCallback(() => {
    return isRestricted(DemoFeature.PASSWORD_RESET);
  }, [isRestricted]);

  const getPasswordResetMessage = useCallback(() => {
    return getMessage(DemoFeature.PASSWORD_RESET);
  }, [getMessage]);

  const logPasswordResetAction = useCallback(
    (action: string, details?: any) => {
      logAction(DemoFeature.PASSWORD_RESET, action, details);
    },
    [logAction]
  );

  return {
    isPasswordResetRestricted: checkPasswordResetRestriction(),
    passwordResetMessage: getPasswordResetMessage(),
    logPasswordResetAction,
  };
};

/**
 * AI API制限用フック（将来用）
 */
export const useAIRestriction = () => {
  const { isRestricted, getMessage, getMockResponse, logAction } = useDemoMode();

  const checkAIRestriction = useCallback(() => {
    return isRestricted(DemoFeature.AI_API);
  }, [isRestricted]);

  const getAIMessage = useCallback(() => {
    return getMessage(DemoFeature.AI_API);
  }, [getMessage]);

  const getAIMockResponse = useCallback(
    (options?: any) => {
      return getMockResponse(ServiceType.AI_API, options);
    },
    [getMockResponse]
  );

  const logAIAction = useCallback(
    (action: string, details?: any) => {
      logAction(DemoFeature.AI_API, action, details);
    },
    [logAction]
  );

  return {
    isAIRestricted: checkAIRestriction(),
    aiMessage: getAIMessage(),
    getAIMockResponse,
    logAIAction,
  };
};

/**
 * LINE通知制限用フック（将来用）
 */
export const useLineRestriction = () => {
  const { isRestricted, getMessage, getMockResponse, logAction } = useDemoMode();

  const checkLineRestriction = useCallback(() => {
    return isRestricted(DemoFeature.LINE_NOTIFICATION);
  }, [isRestricted]);

  const getLineMessage = useCallback(() => {
    return getMessage(DemoFeature.LINE_NOTIFICATION);
  }, [getMessage]);

  const getLineMockResponse = useCallback(
    (options?: any) => {
      return getMockResponse(ServiceType.LINE, options);
    },
    [getMockResponse]
  );

  const logLineAction = useCallback(
    (action: string, details?: any) => {
      logAction(DemoFeature.LINE_NOTIFICATION, action, details);
    },
    [logAction]
  );

  return {
    isLineRestricted: checkLineRestriction(),
    lineMessage: getLineMessage(),
    getLineMockResponse,
    logLineAction,
  };
};
